import { QuestionCircleOutlined } from '@ant-design/icons';
import { Space, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table/Table';
import React, { FC } from 'react';
import { Platform } from '@/infrastructure/utils/app';
import { MATCH_MODE_TEXT, MatchMode } from '@/presentation/components/form/standards/types';
import {
  ConfigTabType,
  FullConfig,
  isConfigManage,
} from '@/presentation/pages/setting/app-deliver-config/api';
import { PLATFORM_OPTIONS } from '@/presentation/pages/setting/app-deliver-config/config-header';
import { ConfigBtn } from '@/presentation/pages/setting/app-deliver-config/config-list/config-btn';
import { ConfigSwitch } from '@/presentation/pages/setting/app-deliver-config/config-list/config-switch';
import { APP_VERSION_MODE_OPTIONS, getForm } from '@/presentation/pages/setting/app-deliver-config/config-modal/edit';
import { getAppVersionModeLabel } from '@/presentation/pages/setting/app-deliver-config/config-modal/view';
import { getOpertor } from '../config-modal/edit/diff-modal';
import style from './index.module.scss';

export const ConfigList: FC<{
  tab: ConfigTabType;
  configList: FullConfig[];
  auth: boolean;
  update: () => void;
  onChange: (page: number, pageSize?: number) => void;
  page: number,
  total: number,
  loading: boolean,
  defaultConfig: string;
  loadDefaultConfig: (platform?: Platform) => void;
}> = (props) => {
  const { tab, configList, auth, update, onChange, page, total, loading, defaultConfig, loadDefaultConfig } = props;

  const data = configList.map((item: FullConfig) => ({ ...item, auth }));

  const renderText = (text: string) => <div style={{ wordBreak: 'break-word' }}>{text}</div>;

  const renderSwitch = (data: FullConfig) => (
    <ConfigSwitch
      tab={tab}
      data={data}
      update={update}
    />
  );

  const renderBtn = (data: FullConfig) => (
    <ConfigBtn
      loadDefaultConfig={loadDefaultConfig}
      tab={tab}
      data={data}
      update={update}
      defaultConfig={defaultConfig}
    />
  );

  const renderFilter = (_: unknown, record: any) => {
    const values = getForm(record);
    const appVersionMode = getAppVersionModeLabel(values?.appVersionMode ?? [])
      ?.filter(item => item !== APP_VERSION_MODE_OPTIONS[0].label);
    let productId = [];
    try {
      productId = JSON.parse(values.productId as string);
    } catch (e) {
      productId = [];
    }
    const viewConfig = [
      { label: '构建类型', value: appVersionMode ?? [], disable: tab === ConfigTabType.COMPLIANCE },
      { label: 'APP版本', value: values?.appVersion ?? [], operator: values?.appVersionOperator ?? MATCH_MODE_TEXT[MatchMode.OR] },
      { label: 'SDK版本', value: values?.sdkVersion ?? [], operator: values?.sdkVersionOperator ??  MATCH_MODE_TEXT[MatchMode.OR] },
      { label: '系统版本', value: values?.system ?? [], operator: values?.osVersionOperator ??  MATCH_MODE_TEXT[MatchMode.OR] },
      { label: '机型', value: values?.model ?? [] },
      { label: '用户白名单', value: values?.uin ?? [] },
      { label: '设备白名单', value: values?.deviceId ?? [] },
      { label: 'App ID', value: productId ?? [], disable: tab === ConfigTabType.COMPLIANCE || tab === ConfigTabType.BUSINESS },
    ].filter(item => !item.disable && item.value.length);

    return <div style={{ wordBreak: 'break-word' }}>
      {viewConfig.length ? viewConfig.map(item => <div>{item.label} { getOpertor(item.operator || 'OR') } [{(item.value as string[] || [])?.join()}]</div>) : '-'}
    </div>;
  };

  const configTableColumns: ColumnsType<FullConfig> = [
    {
      title: '序号',
      dataIndex: 'index',
      width: 70,
      fixed: 'left',
    },
    {
      title: '任务ID',
      dataIndex: 'id',
      width: 80,
    },
    {
      title: '任务名称',
      dataIndex: 'name',
      width: 180,
      render: renderText,
    },
    {
      title: '配置状态',
      width: 100,
      fixed: 'right',
      render: renderSwitch,
    },
    {
      title: (<Space direction={'horizontal'}>
        优先级
        <Tooltip title={'如果有多个配置任务同时满足用户的条件，系统会优先下发优先级最高的配置任务。数值越大，优先级越高。'}>
          <QuestionCircleOutlined style={{ color: '#A3A3A9', fontSize: '14px' }}/>
        </Tooltip>
      </Space>),
      dataIndex: 'priority',
      width: 110,
      fixed: 'right',
    },
    {
      title: (<Space direction={'horizontal'}>
        适用条件
        <Tooltip title={'设置“适用条件”后，只有满足这些条件的用户才有机会拉取该配置任务。如果有多个配置任务同时满足用户的条件，系统会优先下发优先级最高的配置任务。'}>
          <QuestionCircleOutlined style={{ color: '#A3A3A9', fontSize: '14px' }}/>
        </Tooltip>
      </Space>),
      width: 240,
      render: renderFilter,
    },
    {
      title: '创建人',
      dataIndex: 'createUser',
      width: 120,
      fixed: 'right',
    },
    {
      title: '备注',
      dataIndex: 'memo',
      width: 240,
      render: renderText,
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      fixed: 'right',
      render: renderBtn,
    },
  ];

  if (isConfigManage(tab)) {
    configTableColumns.splice(3, 0, {
      title: '平台',
      width: 100,
      dataIndex: 'platform',
      render: platform => PLATFORM_OPTIONS.find(item => item.value === platform)?.label || platform,
    });
  }

  return (
    <div className={style.table} >
      <Table
        dataSource={data}
        columns={configTableColumns}
        rowKey={'id'}
        pagination={{
          current: page,
          defaultPageSize: 10,
          showSizeChanger: true,
          total,
          onChange,
        }}
        loading={loading}
      />
    </div>
  );
};
